// Shared "Summary" invoice/bill PDF builder. Returns a jsPDF doc. Used by
// the contractor's Summary bill export (JobCosting) and the facility's
// invoice print so both sides see the same layout / styling.
//
// Itemized + Full breakdown bill formats stay specialized in JobCosting —
// they're contractor-only.

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { loadImageAsDataUrl } from "./pdfExport";

const money = (n) => {
  const v = typeof n === "number" ? n : parseFloat(n) || 0;
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
};
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const addrLineList = (addr = {}) => {
  const lines = [];
  if (addr.street) {
    const street = addr.suite || addr.building
      ? `${addr.street}, Ste ${addr.suite || addr.building}`
      : addr.street;
    lines.push(street);
  }
  const cityLine = [addr.city, addr.state, addr.zip].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  if (addr.phone) lines.push(addr.phone);
  return lines;
};

/**
 * Build a jsPDF doc for a Summary-format invoice/bill.
 *
 * payload:
 *   orgName, orgAddress, logoUrl   — contractor brand block at top
 *   invoiceNumber                  — INV-YYYY-#### (optional; pre-finalize bills lack it)
 *   jobNumber, jobTitle, jobType   — secondary line under invoice number
 *   date                           — Date obj (default: now)
 *   customerName, customerBilling  — "Bill To" block
 *   locationName, locationAddress  — "Service Location" block
 *   workDescription                — scope-of-work paragraph above totals table
 *   subtotal, taxes, grandTotal    — totals; taxes = [{name, mode, rate, amount}]
 *   taxExempt, taxExemptCert       — when true, tax block renders a single
 *                                    "Sales tax — EXEMPT (Cert ...)" row
 *                                    showing $0.00 in place of the tax list
 *   priorBills                     — for multi-bill jobs: list of bills
 *                                    issued BEFORE this one on the same job.
 *                                    Each: { invoiceNumber, description,
 *                                    grandTotal, finalizedAt, type }. When
 *                                    non-empty, doc renders a "Bills on this
 *                                    job" statement below the boxed Total
 *                                    Due Now + a Total Billed to Date footer.
 *   priorBilled                    — legacy fallback: sum of prior grandTotals.
 *                                    Used when priorBills isn't available
 *                                    (older mirrored docs).
 *   priorBilledLabel               — optional override for the legacy
 *                                    "Previously invoiced on this job" label.
 *   finalizedAt                    — ISO timestamp (optional; rendered below totals)
 *   doc                            — optional existing jsPDF instance to
 *                                    append to (for bulk export bundling).
 *                                    When passed, this invoice gets rendered
 *                                    on a fresh page within that doc instead
 *                                    of in a new jsPDF. Caller owns the doc
 *                                    and is responsible for saving it.
 */
export async function buildInvoicePdfDoc(payload = {}) {
  const {
    orgName = "",
    orgAddress = {},
    logoUrl = "",
    invoiceNumber = "",
    jobNumber = "",
    jobTitle = "",
    jobType = "",
    date = new Date(),
    customerName = "—",
    customerBilling = {},
    locationName = "—",
    locationAddress = {},
    workDescription = "",
    subtotal = 0,
    taxes = [],
    grandTotal = 0,
    taxExempt = false,
    taxExemptCert = "",
    priorBills = [],
    priorBilled = 0,
    priorBilledLabel = "Previously invoiced on this job",
    finalizedAt = "",
    voided = false,
    voidedAt = "",
    voidReason = "",
    doc: existingDoc = null,
  } = payload;
  const hasPriorBills = Array.isArray(priorBills) && priorBills.length > 0;
  const effectivePriorBilled = hasPriorBills
    ? priorBills.reduce((s, b) => s + (b.grandTotal || 0), 0)
    : priorBilled;

  // Bulk export: caller passes the running doc and we append a fresh
  // page for this invoice. Fresh single export: we create our own doc.
  let doc;
  if (existingDoc) {
    doc = existingDoc;
    doc.addPage();
  } else {
    doc = new jsPDF({ unit: "pt", format: "letter" });
  }
  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 40;
  const CONTENT_W = PAGE_W - MARGIN * 2;

  // --- Header: logo + brand block ---
  const logoData = await loadImageAsDataUrl(logoUrl);
  let titleX = MARGIN;
  if (logoData) {
    try {
      doc.addImage(logoData, "AUTO", MARGIN, MARGIN, 80, 60);
      titleX = MARGIN + 90;
    } catch (err) {
      console.warn("invoicePdf: logo addImage failed:", err);
    }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(34, 34, 34);
  doc.text("Invoice", titleX, MARGIN + 18);

  let headerY = MARGIN + 18;
  if (orgName) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(102, 102, 102);
    headerY += 16;
    doc.text(orgName, titleX, headerY);
  }
  doc.setFontSize(10);
  doc.setTextColor(85, 85, 85);
  for (const line of addrLineList(orgAddress)) {
    headerY += 12;
    doc.text(line, titleX, headerY);
  }
  headerY = Math.max(headerY, MARGIN + 60);

  // --- Doc meta: invoice # primary, job # + title beneath. Date right-aligned. ---
  let y = headerY + 28;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(34, 34, 34);
  const dateStr = `Date: ${date.toLocaleDateString()}`;
  doc.text(dateStr, PAGE_W - MARGIN - doc.getTextWidth(dateStr), y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(26, 95, 180);
  if (invoiceNumber) {
    doc.text(`Invoice #${invoiceNumber}`, MARGIN, y);
    y += 18;
    doc.setFontSize(12);
    doc.setTextColor(34, 34, 34);
    const jobLine = `Job #${jobNumber || "—"}${
      jobTitle ? ` — ${jobTitle}` : ""
    }${jobType ? ` (${jobType})` : ""}`;
    doc.text(jobLine, MARGIN, y);
  } else {
    const jobLine = `Job #${jobNumber || "—"}${
      jobTitle ? ` — ${jobTitle}` : ""
    }${jobType ? ` (${jobType})` : ""}`;
    doc.text(jobLine, MARGIN, y);
  }

  // --- Parties: Bill To (left) + Service Location (right) ---
  y += 22;
  const partyStartY = y;
  const colW = (CONTENT_W - 20) / 2;
  const locColX = MARGIN + colW + 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Bill To", MARGIN, y);
  doc.text("Service Location", locColX, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.text(customerName, MARGIN, y);
  doc.text(locationName, locColX, y);
  y += 12;

  const billLines = addrLineList(customerBilling);
  const locLines = addrLineList(locationAddress);

  let billY = y;
  let locY = y;
  if (billLines.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setTextColor(136, 136, 136);
    doc.text("No billing address on file", MARGIN, billY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(34, 34, 34);
    billY += 12;
  } else {
    for (const line of billLines) {
      doc.text(line, MARGIN, billY);
      billY += 12;
    }
  }
  for (const line of locLines) {
    doc.text(line, locColX, locY);
    locY += 12;
  }

  y = Math.max(billY, locY, partyStartY + 38) + 8;

  // --- Description of work (above totals table) ---
  if (workDescription) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    doc.text("Description of work", MARGIN, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    const descLines = doc.splitTextToSize(workDescription, CONTENT_W);
    doc.text(descLines, MARGIN, y);
    y += descLines.length * 13 + 8;
  }

  // --- Totals table: Subtotal + tax/fee lines (each with a faint bottom
  // border so the rows are visually distinct) + Total Due (foot with a
  // thicker top border + larger font). Justin called out the lack of
  // visible breaks; bodyStyles.lineWidth fixes that. ---
  const taxRow = (t) => [
    {
      content: `${t.name || "Tax / fee"}${
        t.mode === "percent" && num(t.rate) ? ` (${num(t.rate)}%)` : ""
      }`,
    },
    { content: money(t.amount), styles: { halign: "right" } },
  ];

  // When the customer is tax-exempt, render a single "EXEMPT" row in
  // place of the per-tax-line list. The tax block still occupies the
  // same row in the table so the document reads as a normal invoice
  // (just with the zero-tax citation).
  const taxBodyRows = taxExempt
    ? [
        [
          {
            content: `Sales tax — EXEMPT${
              taxExemptCert ? ` (Cert ${taxExemptCert})` : ""
            }`,
            styles: { textColor: 120 },
          },
          {
            content: money(0),
            styles: { halign: "right", textColor: 120 },
          },
        ],
      ]
    : taxes.map(taxRow);

  // The Total Due foot row gets a yellow boxed fill when other bills exist
  // on this job — Justin's call: "make Total Due Now more noticeable than
  // already billed so customers don't pay the cumulative number by
  // mistake." Single-bill jobs keep today's clean plain styling.
  const showProminentTotal = hasPriorBills || effectivePriorBilled > 0;
  autoTable(doc, {
    startY: y,
    theme: "plain",
    margin: { left: MARGIN, right: MARGIN },
    body: [
      [
        { content: "Subtotal" },
        { content: money(subtotal), styles: { halign: "right" } },
      ],
      ...taxBodyRows,
    ],
    foot: [
      [
        {
          content: showProminentTotal ? "TOTAL DUE NOW" : "Total Due",
          styles: { fontSize: showProminentTotal ? 14 : 13 },
        },
        {
          content: money(grandTotal),
          styles: {
            halign: "right",
            fontSize: showProminentTotal ? 14 : 13,
          },
        },
      ],
    ],
    styles: { fontSize: 11, cellPadding: 6 },
    bodyStyles: {
      lineWidth: { bottom: 0.4 },
      lineColor: 215,
    },
    footStyles: showProminentTotal
      ? {
          fontStyle: "bold",
          fillColor: [255, 247, 215], // soft warm yellow
          textColor: [50, 35, 0],
          lineWidth: 1.5,
          lineColor: [200, 155, 0],
          cellPadding: 10,
        }
      : {
          fontStyle: "bold",
          fillColor: false,
          textColor: 34,
          lineWidth: { top: 1.25 },
          lineColor: 80,
        },
    columnStyles: { 1: { halign: "right" } },
  });

  // --- "Bills on this job" statement (when priorBills array is supplied) ---
  // List every prior invoice + this one with INV# / description / date /
  // amount, then a "Total billed to date" foot. Visual weight is smaller
  // than the Total Due Now box above so the customer's eye lands on what
  // they owe NOW first, then sees the running statement second.
  if (hasPriorBills) {
    const startY = doc.lastAutoTable.finalY + 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    doc.text("Bills on this job", MARGIN, startY);

    const allBills = [
      ...priorBills,
      {
        invoiceNumber: invoiceNumber || "(this invoice)",
        description:
          workDescription || (jobTitle ? jobTitle : "Current invoice"),
        grandTotal,
        finalizedAt: finalizedAt || new Date().toISOString(),
        isCurrent: true,
      },
    ];
    const billedToDate = allBills.reduce(
      (s, b) => s + (b.grandTotal || 0),
      0
    );
    autoTable(doc, {
      startY: startY + 6,
      theme: "plain",
      margin: { left: MARGIN, right: MARGIN },
      head: [
        [
          "Invoice #",
          "Description",
          { content: "Date", styles: { halign: "right" } },
          { content: "Amount", styles: { halign: "right" } },
        ],
      ],
      body: allBills.map((b) => [
        b.invoiceNumber || "—",
        (b.description || "") +
          (b.isCurrent
            ? " (this invoice)"
            : b.type === "final"
            ? " (final)"
            : ""),
        {
          content: b.finalizedAt
            ? new Date(b.finalizedAt).toLocaleDateString()
            : "",
          styles: { halign: "right" },
        },
        {
          content: money(b.grandTotal || 0),
          styles: {
            halign: "right",
            fontStyle: b.isCurrent ? "bold" : "normal",
          },
        },
      ]),
      foot: [
        [
          {
            content: "Total billed to date",
            colSpan: 3,
            styles: { fontStyle: "bold" },
          },
          {
            content: money(billedToDate),
            styles: { halign: "right", fontStyle: "bold" },
          },
        ],
      ],
      styles: { fontSize: 10, cellPadding: 5 },
      headStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 80,
        lineWidth: { bottom: 0.6 },
        lineColor: 200,
      },
      bodyStyles: {
        lineWidth: { bottom: 0.4 },
        lineColor: 215,
        textColor: 60,
      },
      footStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 50,
        lineWidth: { top: 1 },
        lineColor: 120,
      },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: "auto" },
        2: { cellWidth: 75, halign: "right" },
        3: { cellWidth: 85, halign: "right" },
      },
    });
  } else if (effectivePriorBilled > 0) {
    // Legacy fallback: older mirror docs carried priorBilled (a sum) but
    // not the priorBills array. Render the simple two-row block we shipped
    // earlier so those docs still show running context.
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 14,
      theme: "plain",
      margin: { left: MARGIN, right: MARGIN },
      body: [
        [
          { content: priorBilledLabel, styles: { textColor: 80 } },
          {
            content: money(effectivePriorBilled),
            styles: { halign: "right", textColor: 80 },
          },
        ],
        [
          {
            content: "Total billed to date",
            styles: { fontStyle: "bold" },
          },
          {
            content: money(effectivePriorBilled + grandTotal),
            styles: { halign: "right", fontStyle: "bold" },
          },
        ],
      ],
      styles: { fontSize: 11, cellPadding: 5 },
      bodyStyles: {
        lineWidth: { bottom: 0.4 },
        lineColor: 215,
      },
      columnStyles: { 1: { halign: "right" } },
    });
  }

  // --- Finalized stamp below totals ---
  if (finalizedAt) {
    const finalY = doc.lastAutoTable.finalY + 18;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(102, 102, 102);
    doc.text(
      `Finalized ${new Date(finalizedAt).toLocaleDateString()}`,
      MARGIN,
      finalY
    );
  }

  // VOIDED watermark (Phase A). Drawn last so it overlays every other
  // element. Red diagonal banner across the center of every page +
  // top-of-page banner with the void reason / date. Renders only when
  // the bill is voided — non-voided exports unchanged.
  if (voided) {
    const pageCount = doc.internal.getNumberOfPages();
    const PAGE_H = doc.internal.pageSize.getHeight();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);

      // Top-of-page red banner with reason + date.
      doc.setFillColor(254, 226, 226);
      doc.rect(0, 0, PAGE_W, 24, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(185, 28, 28);
      const dateStr2 = voidedAt
        ? new Date(voidedAt).toLocaleDateString()
        : "";
      doc.text(
        `VOIDED${voidReason ? ` — ${voidReason.replace(/_/g, " ")}` : ""}${
          dateStr2 ? ` (${dateStr2})` : ""
        }`,
        MARGIN,
        16
      );

      // Diagonal "VOID" watermark centered. Light red so the doc is
      // still readable but the void status is unmistakable.
      doc.saveGraphicsState && doc.saveGraphicsState();
      try {
        doc.setGState && doc.setGState(new doc.GState({ opacity: 0.18 }));
      } catch (err) {
        // Older jsPDF — skip the alpha, watermark still draws (just denser).
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(160);
      doc.setTextColor(185, 28, 28);
      doc.text("VOID", PAGE_W / 2, PAGE_H / 2, {
        angle: 35,
        align: "center",
        baseline: "middle",
      });
      doc.restoreGraphicsState && doc.restoreGraphicsState();
    }
    // Reset to last page so callers writing further (none today) don't
    // surprise themselves with the page pointer.
    doc.setPage(pageCount);
  }

  return doc;
}
